// @ts-nocheck

import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { animated } from 'react-spring/web.cjs';
import styled, { css } from 'styled-components';

import { Target, isIOSEmbeddedAndStandalone } from '../../client';
import config from '../../config/app.config';
import { StreamModes } from '../../constants/picture-in-picture';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getAppDOMElement } from '../../DOMElements';
import useDesktopHorizontalScroll from '../../hooks/useDesktopHorizontalScroll';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import useMouseDragAndResize from '../../hooks/useMouseDragAndResize';
import usePictureInPicture from '../../hooks/usePictureInPicture';
import useTouchDragAndResize from '../../hooks/useTouchDragAndResize';
import { getLang } from '../../i18n/l10n';
import { getPositionOffset, getDimensions } from '../../lib/core/dom/dimensions';
import { LiveVideoPlayer } from '../../modules/immersive/components/video-player';
import theme from '../../styled-components/dark';
import isMatch from '../../utils/routes/isMatch';
import TvBarContainer from '../TvBar/TvBarContainer';

const Wrapper = styled(animated.div)`
  ${({ $isPipCustomActive, $isPipNativeActive, $isStreamMatchOpen }) =>
    $isPipCustomActive && !$isPipNativeActive
      ? css`
          cursor: grab;
          z-index: ${theme.ZIndices.floatingVideoPip};

          > div {
            ${$isStreamMatchOpen
              ? css`
                  border-radius: 0.4em;
                  overflow: hidden;
                `
              : undefined}
          }
        `
      : css`
          cursor: auto;
          z-index: ${Target.__MOBILE__ ? theme.ZIndices.tabBar - 1 : theme.ZIndices.floatingVideo};

          > div {
            border-radius: unset;
            overflow: auto;
          }
        `}

  ${({ $hideTvForNativePip }) =>
    $hideTvForNativePip
      ? css`
          // hide floating video if native Pip is active
          visibility: hidden;
          pointer-events: none; // allow interaction under hidden placeholder
        `
      : undefined}


  ${({ $hasStickyOffsetTop, $isPipCustomActive }) =>
    Target.__MOBILE__ && $hasStickyOffsetTop && !$isPipCustomActive
      ? css`
          margin-top: constant(safe-area-inset-top);
          margin-top: env(safe-area-inset-top);
          transition: 0.3s margin ease;
        `
      : undefined}
`;

const IframeWrapper = styled.div`
  position: relative;
  padding-top: 56.25%;
`;

const StyledIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const useDragAndResize = (floatingVideoRef, isDragAndResizeEnabled) => {
  // hande desktop interaction
  const { isDragging: isMouseDragging } = useMouseDragAndResize(
    floatingVideoRef.current,
    isDragAndResizeEnabled && !Target.__MOBILE__,
  );
  // hande mobile interaction
  const {
    bind,
    style,
    isDragging: isTouchDragging,
  } = useTouchDragAndResize(floatingVideoRef.current, isDragAndResizeEnabled && Target.__MOBILE__);

  // lock scroll when dragging
  const isDragging = Target.__MOBILE__ ? isTouchDragging : isMouseDragging;
  useLockBodyScroll(Target.__MOBILE__ ? getAppDOMElement() : document.body, !isDragging);

  const bindGesture = Target.__MOBILE__ ? bind() : null;
  const styleGesture = Target.__MOBILE__ ? style : null;
  return { bindGesture, styleGesture };
};

/**
 * Custom version of Picture in Picture mode
 *
 * @return {Node} A wrapper with the immersive player or the WTV iframe and controls if necessary
 */
const PiPHandler = () => {
  const {
    floatingVideoRef,
    isPipCustomActive,
    type,
    matchId: streamMatchId,
    streamData,
    placeholderRef,
    closeStream,
    stickyOffsetTop,
    getStreamWtvOpen,
    isPipNativeActive,
    isSameMatchRouteAndPipNativeActive,
    isMatchRouteRelativeToStream,
    isPipEnabled,
    isWTVEnabled,
    setIsSameMatchRouteAndPipNativeActive,
    setPipCustomIsActive,
    jwt,
  } = usePictureInPicture();

  const { width: viewportWidth } = useViewportContext();
  const location = useLocation();

  const isStreamWtvOpen = getStreamWtvOpen();
  const isStreamMatchOpen = streamMatchId !== null && type === StreamModes.MATCH;
  const isRouteMatch = isMatch(location.pathname);

  const showStream = isPipCustomActive || isStreamMatchOpen || isStreamWtvOpen;
  const hideTvForNativePip = isStreamMatchOpen && isPipCustomActive && isPipNativeActive && Target.__MOBILE__;
  const isDragAndResizeEnabled = isPipCustomActive && !isSameMatchRouteAndPipNativeActive;

  const { bindGesture, styleGesture } = useDragAndResize(floatingVideoRef, isDragAndResizeEnabled);
  // position the video on  the placeholder by default
  const placeVideoOnPlaceholder = useCallback(() => {
    const floatingVideo = floatingVideoRef.current;
    const placeholder = placeholderRef?.current;

    if (!floatingVideo || !placeholder) {
      return;
    }

    const { width: placeholderWidth } = getDimensions(placeholder);
    const { left, top } = getPositionOffset(placeholder, getAppDOMElement());
    const width = Target.__MOBILE__ ? viewportWidth : placeholderWidth;
    const hasStickyOffsetTop = stickyOffsetTop !== null && type === StreamModes.MATCH;

    floatingVideo.style.top = `${hasStickyOffsetTop ? stickyOffsetTop : top}px`;
    floatingVideo.style.left = `${left}px`;
    floatingVideo.style.width = `${width}px`;
    floatingVideo.style.height = 'auto';
    floatingVideo.style.position = hasStickyOffsetTop ? 'fixed' : 'absolute';
    floatingVideo.style.transform = 'none';
  }, [floatingVideoRef, placeholderRef, stickyOffsetTop, type, viewportWidth]);

  // position the video on fixed when we activate the picture in picture mode
  const placePip = useCallback(() => {
    const floatingVideo = floatingVideoRef.current;

    if (!floatingVideo) {
      return;
    }

    floatingVideo.style.top = '0';
    floatingVideo.style.left = '0';
    floatingVideo.style.height = 'auto';
    floatingVideo.style.position = 'fixed';
  }, [floatingVideoRef]);

  useEffect(() => {
    if (!isStreamMatchOpen && !isStreamWtvOpen) {
      return;
    }

    // place the video on the middle column only if the PiP is not active OR
    // ...we are in the same match and native PiP is active
    if (!isPipCustomActive || isSameMatchRouteAndPipNativeActive) {
      placeVideoOnPlaceholder();
    } else {
      // place the video on the bottom right corner
      placePip();
    }
  }, [
    isPipCustomActive,
    isStreamMatchOpen,
    placePip,
    placeVideoOnPlaceholder,
    isStreamWtvOpen,
    isSameMatchRouteAndPipNativeActive,
  ]);

  // closes the stream if we are not on a match route AND not Pip mode
  useEffect(() => {
    if (isStreamMatchOpen && !isRouteMatch && !isPipCustomActive) {
      closeStream();
    }
  }, [closeStream, isStreamMatchOpen, isRouteMatch, isPipCustomActive]);

  // close the WTV PiP stream and set the wtv bar to off when we disable the WinaTV from the back office
  useEffect(() => {
    if (!isWTVEnabled && isPipCustomActive && type === StreamModes.TV) {
      closeStream();
    }
  }, [closeStream, isPipCustomActive, isWTVEnabled, type]);

  // detects if we are on the same match route and that the native pip is active
  useEffect(() => {
    setIsSameMatchRouteAndPipNativeActive(isMatchRouteRelativeToStream && isPipNativeActive);
  }, [isMatchRouteRelativeToStream, isPipNativeActive, setIsSameMatchRouteAndPipNativeActive]);

  // NB: ONLY for native pip use case
  // if we close the native Pip on the same match route, we set the custom pip to false in order to
  // ...place the placeholder on the middle column
  useEffect(() => {
    if (isPipEnabled && isMatchRouteRelativeToStream && !isPipNativeActive) {
      setPipCustomIsActive(false);
    }
  }, [isPipEnabled, isMatchRouteRelativeToStream, isPipNativeActive, setPipCustomIsActive]);

  const onDesktopHorizontalScroll = () => {
    if (isStreamMatchOpen && !isPipCustomActive) {
      placeVideoOnPlaceholder();
    }
  };

  // replaces the video to its correct position after the wheel event occures
  useDesktopHorizontalScroll(onDesktopHorizontalScroll);

  // prevent native zoom when we are in safari browser
  // The native zoom doesn't allow the user to do a pinch properly
  useEffect(() => {
    const gestureHandler = (e) => {
      e.preventDefault();
    };

    window.addEventListener('gesturestart', gestureHandler);
    window.addEventListener('gesturechange', gestureHandler);

    return () => {
      window.removeEventListener('gesturestart', gestureHandler);
      window.removeEventListener('gesturechange', gestureHandler);
    };
  }, []);

  let tvUrl = `${config.webHost[getLang()]}/betting/tv/get_iframe.php`;

  if (isIOSEmbeddedAndStandalone) {
    tvUrl += '?iframe=1';
  }

  return showStream ? (
    <Wrapper
      ref={floatingVideoRef}
      $hideTvForNativePip={hideTvForNativePip}
      $isPipCustomActive={isPipCustomActive}
      $isPipNativeActive={isPipNativeActive}
      $isStreamMatchOpen={isStreamMatchOpen}
      $isStreamWtvOpen={isStreamWtvOpen}
      $hasStickyOffsetTop={stickyOffsetTop !== null && type === StreamModes.MATCH}
      style={styleGesture}
      {...bindGesture}
    >
      {isStreamMatchOpen ? (
        <LiveVideoPlayer key={streamMatchId} matchId={streamMatchId} streamData={streamData} jwt={jwt} />
      ) : null}
      {isStreamWtvOpen ? (
        <>
          {isPipCustomActive ? <TvBarContainer /> : null}
          <IframeWrapper>
            <StyledIframe frameBorder="0" height="100%" scrolling="no" src={tvUrl} title="Winamax TV" width="100%" />
          </IframeWrapper>
        </>
      ) : null}
    </Wrapper>
  ) : null;
};

export default PiPHandler;
